import PropTypes from 'prop-types';
import seoTexts from 'texts/seo.json';

import ItemListJsonLd from 'components/Seo/ItemListJsonLd';
import SchemaJsonLd from 'components/Seo/SchemaJsonLd';

import { getOfferImageLink } from 'lib/image';
import { toAbsoluteLink, toOfferLink, toStoreOffersLink } from 'lib/links';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';
import {
  formatItemListAsJsonLd,
  formatProductAsJsonLd,
  fromStatusToAvailabilityJsonLd,
} from 'lib/schemas';
import { toFormattedJsonLdString } from 'lib/string';

const ITEM_LIST_LIMIT = 12;

const isRenderable = (offer) => Boolean(offer?.offerSlug && offer?.offerId);

const renderOfferAsItemListEntry = (
  {
    categoryName,
    offerId,
    offerOldPrice,
    offerPhoto,
    offerPrice,
    offerPriceType,
    offerSlug,
    offerStatusName,
    offerTags,
    offerTitle,
    storeName,
    storeSlug,
  },
  index
) =>
  formatItemListAsJsonLd({
    item: formatProductAsJsonLd({
      images: [getOfferImageLink(offerPhoto, 268)],
      offers: [
        {
          price: offerPrice,
          priceCurrency: 'BRL',
          availability: fromStatusToAvailabilityJsonLd(
            offerStatusName,
            offerTags
          ),
          url: toOfferLink(offerSlug),
          seller: {
            name: storeName,
            url: toStoreOffersLink(storeSlug),
          },
          offerCount: 1,
          category: categoryName,
          lowPrice: toFormattedOfferPrice(offerPrice, offerPriceType),
          highPrice: toFormattedOfferOldPrice(offerOldPrice, offerPriceType),
        },
      ],
      productName: toFormattedJsonLdString(offerTitle),
      sku: offerId,
    }),
    position: index + 1,
    url: toAbsoluteLink(toOfferLink(offerSlug)),
  });

const OffersTimelineSchema = ({ canonicalUrl, name = null, offers }) => {
  const renderableOffers = offers
    .filter(isRenderable)
    .slice(0, ITEM_LIST_LIMIT);
  const schemaName = name || seoTexts.default.title;

  return (
    <>
      <SchemaJsonLd
        id="home-collection-page"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: schemaName,
          url: canonicalUrl,
        }}
      />
      <ItemListJsonLd
        description={schemaName}
        id="home-offers-itemlist"
        items={renderableOffers}
        name={schemaName}
        renderItemAsSchema={renderOfferAsItemListEntry}
      />
    </>
  );
};

OffersTimelineSchema.propTypes = {
  canonicalUrl: PropTypes.string.isRequired,
  name: PropTypes.string,
  offers: PropTypes.arrayOf(
    PropTypes.shape({
      offerId: PropTypes.number,
      offerSlug: PropTypes.string,
    })
  ).isRequired,
};

export default OffersTimelineSchema;
